import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getMessaging, onMessage } from "firebase/messaging";
import {
  notificationNavigation,
  useResolveNotificationUrl,
  PENDING_NOTIFICATION_KEY,
} from "utils/NotificationNavigation";
import app from "utils/firebase";

export const useNotificationNavigation = () => {
  const navigate = useNavigate();
  const resolveUrl = useResolveNotificationUrl();
  const messagingRef = useRef(null);

  useEffect(() => {
    // -------------------------------------------------
    // 1. Handle pending notification after account switch
    // -------------------------------------------------
    try {
      const pendingStr = sessionStorage.getItem(PENDING_NOTIFICATION_KEY);
      if (pendingStr) {
        sessionStorage.removeItem(PENDING_NOTIFICATION_KEY);
        const notificationData = JSON.parse(pendingStr);
        const targetUrl =
          notificationNavigation(notificationData) || "/drive/home";

        navigate(targetUrl);
        localStorage.setItem(
          "lastNotification",
          JSON.stringify(notificationData),
        );
      }
    } catch (err) {
      console.error("Failed to process pending notification", err);
    }

    // -------------------------------------------------
    // 2. Handle notification opened via query parameter
    // -------------------------------------------------
    const urlParams = new URLSearchParams(window.location.search);
    const payloadStr = urlParams.get("notification_payload");
    if (payloadStr) {
      try {
        const notificationData = JSON.parse(payloadStr);
        const targetUrl =
          notificationNavigation(notificationData) || "/drive/home";

        // Clean up the URL
        urlParams.delete("notification_payload");
        const newSearch = urlParams.toString();
        const newUrl =
          window.location.pathname +
          (newSearch ? "?" + newSearch : "") +
          window.location.hash;
        window.history.replaceState({}, document.title, newUrl);

        navigate(targetUrl);
        localStorage.setItem(
          "lastNotification",
          JSON.stringify(notificationData),
        );
      } catch (err) {
        console.error("Failed to parse notification payload", err);
      }
    }

    // -------------------------------------------------
    // 3. Initialize Firebase Messaging
    // -------------------------------------------------
    if (!messagingRef.current) {
      messagingRef.current = getMessaging(app);
    }

    const messaging = messagingRef.current;

    // Background / service-worker click (tab already open)
    const handleServiceWorkerMessage = async (event) => {
      console.log("Message from service worker:", event.data);

      if (event.data && event.data.type === "NOTIFICATION_CLICK") {
        const { notificationData } = event.data;

        const targetUrl = await resolveUrl(notificationData || {});

        if (targetUrl) {
          console.log("Navigating to:", targetUrl);
          navigate(targetUrl);
        }

        if (notificationData) {
          localStorage.setItem(
            "lastNotification",
            JSON.stringify(notificationData),
          );
        }
      }
    };

    // Foreground push
    const handleForegroundMessage = (payload) => {
      console.log("Foreground message received:", payload);

      if (Notification.permission === "granted") {
        const targetUrl =
          notificationNavigation(payload.data) || "/drive/home";

        const notification = new Notification(
          payload.notification?.title || "New Notification",
          {
            body: payload.notification?.body || "You have a new notification",
            icon: window.location.origin + "/logo192.png",
            data: {
              url: targetUrl,
              ...payload.data,
            },
          },
        );

        notification.onclick = async () => {
          window.focus();
          const finalUrl = await resolveUrl(payload.data || {});
          if (finalUrl) {
            navigate(finalUrl);
          }
          notification.close();
        };
      }
    };

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener(
        "message",
        handleServiceWorkerMessage,
      );
    }

    const unsubscribe = onMessage(messaging, handleForegroundMessage);

    // Service worker registration
    const registerServiceWorker = async () => {
      if ("serviceWorker" in navigator) {
        try {
          const registration = await navigator.serviceWorker.register(
            "/firebase-messaging-sw.js",
          );
          console.log(
            "Service Worker registered for domain:",
            window.location.origin,
            registration,
          );
        } catch (error) {
          console.error("Service Worker registration failed:", error);
        }
      }
    };

    registerServiceWorker();

    return () => {
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.removeEventListener(
          "message",
          handleServiceWorkerMessage,
        );
      }
      unsubscribe();
    };
  }, [navigate, resolveUrl]);
};